import React, { useEffect, useState } from "react";
import {
	Box,
	Button,
	Card,
	CardContent,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	IconButton,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Tooltip,
	Typography,
} from "@material-ui/core";
import { useHistory } from "react-router-dom";
import { PageTitle } from "../../../components/PageTitle";
import { BackButton } from "../../../components/BackButton";
import { useAlert } from "../../../hooks/useAlert";
import { routes } from "../../../routes";
import { LeakCheckForm, LeakCheckFormType } from "../../../entities/business/LeakCheckFormTemplate";
import { LeakCheckService, CreateLeakCheckFormRequest } from "../../../services/business/LeakCheckService";
import OverlayLoadingScreen from "../../../components/OverlayLoadingScreen";
import AddIcon from "@material-ui/icons/Add";
import DeleteIcon from "@material-ui/icons/Delete";
import FileCopyIcon from "@material-ui/icons/FileCopy";

export function LeakCheckFormsListPage() {
	const alert = useAlert();
	const history = useHistory();
	const [forms, setForms] = useState<LeakCheckForm[]>([]);
	const [loading, setLoading] = useState(true);
	const [createDialogOpen, setCreateDialogOpen] = useState(false);
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
	const [formToDelete, setFormToDelete] = useState<LeakCheckForm | null>(null);
	const [newFormName, setNewFormName] = useState("");
	const [creatingForm, setCreatingForm] = useState(false);

	// Load forms on component mount
	useEffect(() => {
		const loadForms = async () => {
			setLoading(true);
			try {
				const result = await LeakCheckService.getAllForms();
				if (result.success) {
					setForms(result.data);
				} else {
					alert.serverError(result);
				}
			} catch (error) {
				alert.error("Failed to load forms");
			} finally {
				setLoading(false);
			}
		};
		loadForms();
	}, [alert]);

	const handleCreateForm = async () => {
		if (!newFormName.trim()) {
			alert.error("Form name is required");
			return;
		}

		setCreatingForm(true);
		try {
			const createRequest: CreateLeakCheckFormRequest = {
				name: newFormName,
				type: LeakCheckFormType.Custom,
			};

			const result = await LeakCheckService.createForm(createRequest);
			if (result.success) {
				setForms([...forms, result.data]);
				closeCreateDialog();
				history.push(routes.business.resolve.leakCheckFormDetailPage(result.data.id));
			} else if (result.validation) {
				alert.validation(result);
			} else {
				alert.serverError(result);
			}
		} catch (error) {
			alert.error("Failed to create form");
		} finally {
			setCreatingForm(false);
		}
	};

	const handleDuplicateForm = async (form: LeakCheckForm) => {
		setCreatingForm(true);
		try {
			const createRequest: CreateLeakCheckFormRequest = {
				name: `${form.name} (Copy)`,
				type: form.type,
				customerInfo: form.customerInfo,
				tankCheck: form.tankCheck,
				pressureTests: form.pressureTests,
				regulatorCheck: form.regulatorCheck,
				customSections: form.customSections,
			};

			const result = await LeakCheckService.createForm(createRequest);
			if (result.success) {
				alert.success("Form duplicated successfully");
				setForms([...forms, result.data]);
				history.push(routes.business.resolve.leakCheckFormDetailPage(result.data.id));
			} else if (result.validation) {
				alert.validation(result);
			} else {
				alert.serverError(result);
			}
		} catch (error) {
			alert.error("Failed to duplicate form");
		} finally {
			setCreatingForm(false);
		}
	};

	const handleDeleteForm = async () => {
		if (!formToDelete) return;
		const result = await LeakCheckService.deleteForm(formToDelete.id);
		if (result.success) {
			setForms(forms.filter((f) => f.id !== formToDelete.id));
			alert.success("Form deleted successfully");
			closeDeleteDialog();
		} else if (result.validation) {
			alert.validation(result);
		} else {
			alert.serverError(result);
		}
	};

	const openCreateDialog = () => {
		setNewFormName("");
		setCreateDialogOpen(true);
	};

	const closeCreateDialog = () => {
		setCreateDialogOpen(false);
		setNewFormName("");
	};

	const openDeleteDialog = (form: LeakCheckForm) => {
		setFormToDelete(form);
		setDeleteDialogOpen(true);
	};

	const closeDeleteDialog = () => {
		setDeleteDialogOpen(false);
		setFormToDelete(null);
	};

	const navigateToForm = (formId: string) => {
		history.push(routes.business.resolve.leakCheckFormDetailPage(formId));
	};

	const navigateToBusinessPage = () => {
		history.push(routes.business.resolve.companyProfilePage());
	};

	if (loading) {
		return <OverlayLoadingScreen />;
	}

	return (
		<Grid container spacing={3}>
			<PageTitle title="Leak Check Forms" />
			<Grid item xs={12}>
				<Box mb={3}>
					<Grid container alignItems="center" spacing={2}>
						<Grid item>
							<BackButton onClick={navigateToBusinessPage} />
						</Grid>
						<Grid item>
							<Typography variant="h2">Leak Check Forms</Typography>
						</Grid>
					</Grid>
				</Box>
			</Grid>

			<Grid item xs={12}>
				<Box mb={3} display="flex" justifyContent="flex-end">
					<Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={openCreateDialog}>
						Create New Form
					</Button>
				</Box>
			</Grid>

			<Grid item xs={12}>
				<Card>
					<CardContent>
						{forms.length === 0 ? (
							<Box textAlign="center" py={4}>
								<Typography variant="h6" color="textSecondary" gutterBottom>
									No forms found
								</Typography>
								<Typography variant="body2" color="textSecondary" paragraph>
									Create your first leak check form to get started
								</Typography>
								<Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={openCreateDialog}>
									Create New Form
								</Button>
							</Box>
						) : (
							<TableContainer component={Paper}>
								<Table>
									<TableHead>
										<TableRow>
											<TableCell>Form Name</TableCell>
											<TableCell>Status</TableCell>
											<TableCell>Created</TableCell>
											<TableCell>Last Modified</TableCell>
											<TableCell align="right">Actions</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{forms.map((form) => (
											<TableRow key={form.id} hover onClick={() => navigateToForm(form.id)}>
												<TableCell onClick={() => navigateToForm(form.id)} style={{ cursor: "pointer" }}>
													{form.name}
												</TableCell>
												<TableCell>
													{form.isActive ? (
														<Box component="span" bgcolor="success.main" color="white" px={1} py={0.5} borderRadius={4} fontSize="0.75rem">
															Active
														</Box>
													) : (
														<Box component="span" bgcolor="text.disabled" color="white" px={1} py={0.5} borderRadius={4} fontSize="0.75rem">
															Inactive
														</Box>
													)}
												</TableCell>
												<TableCell>{form.createdDate.toLocaleDateString()}</TableCell>
												<TableCell>{form.lastModifiedDate.toLocaleDateString()}</TableCell>
												<TableCell align="right">
													<Tooltip title="Duplicate Form">
														<IconButton
															size="small"
															onClick={(e) => {
																e.stopPropagation();
																handleDuplicateForm(form);
															}}
															style={{ marginRight: 16 }}
															disabled={creatingForm}
														>
															<FileCopyIcon fontSize="small" />
														</IconButton>
													</Tooltip>
													<Tooltip title="Delete Form">
														<IconButton
															size="small"
															onClick={(e) => {
																e.stopPropagation();
																openDeleteDialog(form);
															}}
															disabled={creatingForm}
														>
															<DeleteIcon fontSize="small" />
														</IconButton>
													</Tooltip>
												</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</TableContainer>
						)}
					</CardContent>
				</Card>
			</Grid>

			{/* Create Form Dialog */}
			<Dialog open={createDialogOpen} onClose={closeCreateDialog} maxWidth="sm" fullWidth>
				<DialogTitle>Create New Form</DialogTitle>
				<DialogContent>
					<Box my={2}>
						<TextField
							label="Form Name"
							value={newFormName}
							onChange={(e) => setNewFormName(e.target.value)}
							fullWidth
							required
							autoFocus
							variant="outlined"
							placeholder="Enter form name"
							disabled={creatingForm}
						/>
					</Box>
				</DialogContent>
				<DialogActions>
					<Button onClick={closeCreateDialog} disabled={creatingForm}>
						Cancel
					</Button>
					<Button onClick={handleCreateForm} color="primary" variant="contained" disabled={!newFormName.trim() || creatingForm}>
						Create
					</Button>
				</DialogActions>
			</Dialog>

			{/* Delete Form Confirmation Dialog */}
			<Dialog open={deleteDialogOpen} onClose={closeDeleteDialog} maxWidth="sm" fullWidth>
				<DialogTitle>Delete Form</DialogTitle>
				<DialogContent>
					<Typography>Are you sure you want to delete the form "{formToDelete?.name}"? This action cannot be undone.</Typography>
				</DialogContent>
				<DialogActions>
					<Button onClick={closeDeleteDialog}>Cancel</Button>
					<Button onClick={handleDeleteForm} color="secondary" variant="contained">
						Delete
					</Button>
				</DialogActions>
			</Dialog>
		</Grid>
	);
}
